import { AloftToolkitBlob, BlobRef, expected } from '@aloft/toolkit-shared';
import { useMutation } from '@tanstack/react-query';

import { ToolkitContext, useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { uploadBlob } from '../../../hooks/api/uploadBlob.ts';

const storeOrSkip = (
  f: File | undefined,
  ctx: ToolkitContext,
): Promise<AloftToolkitBlob.Type | undefined> =>
  f ? uploadBlob(f, f?.name, ctx) : Promise.resolve(undefined);

const blobResultToReference = (input?: AloftToolkitBlob.Type): BlobRef | undefined => {
  if (input === undefined) return input;

  return {
    filename: expected(input.name),
    blobID: input.id,
  };
};

type UploadQAFilesParams = {
  reportPDF: File;
  orderDetailsPDF?: File;
  loePDF?: File;
  purchaseContractPDF?: File;
};

type UploadQAFilesReturn = {
  reportPDF: BlobRef;
  orderDetailsPDF?: BlobRef;
  loePDF?: BlobRef;
  purchaseContractPDF?: BlobRef;
};

export const useUploadQAFiles = () => {
  const ctx = useToolkitContext();

  const uploadQAFiles = async (params: UploadQAFilesParams): Promise<UploadQAFilesReturn> => {
    const { reportPDF, loePDF, purchaseContractPDF, orderDetailsPDF } = params;

    const [reportRes, orderDetailsRes, loeRes, purchaseContractRes] = await Promise.all([
      uploadBlob(reportPDF, reportPDF.name, ctx),
      storeOrSkip(orderDetailsPDF, ctx),
      storeOrSkip(loePDF, ctx),
      storeOrSkip(purchaseContractPDF, ctx),
    ]);

    return {
      reportPDF: { filename: expected(reportRes.name), blobID: reportRes.id },
      orderDetailsPDF: blobResultToReference(orderDetailsRes),
      loePDF: blobResultToReference(loeRes),
      purchaseContractPDF: blobResultToReference(purchaseContractRes),
    };
  };

  return useMutation({
    mutationFn: uploadQAFiles,
  });
};
